/* global UA */

/* External */
import React, { useEffect, useRef } from 'react';
import {
  Route, Switch, Redirect, useLocation,
} from 'react-router-dom';
import { withRouter } from 'react-router';
import { connect, useDispatch, useSelector } from 'react-redux';
import getL from 'lodash-es/get';
import LoadingBar from 'react-redux-loading-bar';
import { datadogRum } from '@datadog/browser-rum';
import jwtDecode from 'jwt-decode';
import classNames from 'classnames';
import PropTypes from 'prop-types';

/* Components */
import { rtkQueryTags } from '@dtvgo/rtk-query-api';
import { useGtmContext } from '../components/GtmContext';
import Landing from '../landing/Landing';
import Home from '../home/Home';
import PrivateRoute from '../privateRoute/PrivateRoute';
import IntlProviderWrapper from '../IntlProviderWrapper';
import ToastNotification from '../components/notification/toast/ToastNotification';
import BarNotification from '../components/notification/barNotification/BarNotification';
import SlowConnection from './SlowConnection';
import ErrorPage from '../errorPage/ErrorPage';
import TermsAndConditions from '../termsAndConditions/TermsAndConditions';
import TermsOfService from '../termsOfService/TermsOfService';
import ScrollToTop from '../components/scrollToTop/ScrollToTop';
import ExpiredSession from '../pages/expiredSession/ExpiredSession';
import InsertPinTV from '../pinLogin/InsertPin';
import ResultInsertPin from '../pinLogin/ResultInsertPin';
import ChromecastConnectionBar from '../components/chromecastConnectionBar/ChromecastConnectionBar';
import SearchOverlay from '../searchOverlay/SearchOverlay';
import Modal from '../components/modal/Modal';
import LivePlayer from '../player/vrio/VrioLivePlayer';
import VodPlayer from '../player/vrio/VrioVodPlayer';
import UserProfile from '../pagesProfile/managementProfile/UserProfile';
import UserProfileManagement from '../pagesProfile/managementProfile/UserProfileManagement';
import UserProfileEdit from '../pagesProfile/managementProfile/UserProfileEdit';
import UserProfileCreate from '../pagesProfile/managementProfile/UserProfileCreate';
import FifaVideoPlayer from '../components/FifaVideoPlayer/FifaVideoPlayer';
import Onboarding from '../pagesProfile/onboarding/Onboarding';
import ProfilesErrorPage from '../pagesProfile/profilesErrorPage/ProfilesErrorPage';
import ProfileContentBlock from '../pagesProfile/profileContentBlock/ProfileContentBlock';
import AuthError from '../user/AuthError';
import { refreshIdToken } from '../services/auth';

/* Ott-common */

/* Styles */
import './App.scss';
import '../theme/colors';
import '../theme/fonts';

/* Actions */
import { setAEMConfiguration } from '../state/global/globalSlice';
import { setProfiles, setSelectedProfile } from '../state/profile/reducer';
import { api } from '../state/configureStore';

/* Other */
import { Logout } from '../user/Logout';
import {
  DEFAULT_VALUES,
  IS_BRAZIL,
  isUserLoggedIn,
  LOGOUT_URL,
  PATHS,
  PIN_LOGIN_URLS_BR,
  PIN_LOGIN_URLS,
  setNuanceDataVariable,
  TERMS_AND_CONDITIONS_URL,
  TERMS_OF_SERVICE_URL,
  NOTIFICATIONS_PERMISSION_LOCAL_STORAGE_NAME,
  TIME_UNITS_AS_MILLISECONDS,
  AEM_PLATFORM,
  getAuthTokensData,
  ROUTES,
  gtmConstantData,
  isBrazil,
  setEncryptedData,
  getEncryptedData,
  encryptAllData,
  alcatrazFeatureFlag,
  decryptAllDataifNeeded,
  isMobile,
} from '../utils';
import { backendService } from '../services';
import {
  CURRENT_URL, PDP_URL, PREVIOUS_URL, PROFILES_STORAGE,
} from '../utils/constants';
import { ChromecastProvider } from '../components/chromecastConnectionBar';
import { MqttProvider } from '../components/mqtt';
import { SELF_CARE } from '../utils/urls/apiConfig';

const CHECK_STATUS_CLIENT_MAX_CALLS = 3;
let checkStatusClientCallCount = 0;

function App({
  path, country, user, location, userCountry,
}) {
  const dispatch = useDispatch();
  const locationValue = useLocation();
  const pinLoginUrls = IS_BRAZIL ? { ...PIN_LOGIN_URLS_BR } : { ...PIN_LOGIN_URLS };
  const { setGtmUserData } = useGtmContext();
  const { idToken } = getAuthTokensData();
  const {
    data: AEMConfiguration,
    isLoading: isAEMConfigurationLoading,
    isError: isAEMConfigurationError,
  } = api.useGetAEMConfigurationQuery(AEM_PLATFORM);
  const aemRequestInterval = useRef();
  const selectedProfile = useSelector((state) => state.profile.selectedProfile);

  useEffect(() => {
    async function onAppLoad() {
      if (checkStatusClientCallCount >= CHECK_STATUS_CLIENT_MAX_CALLS) {
        return;
      }
      checkStatusClientCallCount += 1;

      console.log('checkStatusClientCallCount', checkStatusClientCallCount);

      const statusApi = await backendService.customer.v1.checkStatusClient();
      if (
        statusApi
        && statusApi?.status === 200
        && statusApi?.data
        && statusApi?.data?.shouldRefresh) {
        refreshIdToken();
        console.warn('Token reset by status api');
      }
    }

    if (isUserLoggedIn()) {
      onAppLoad();
    }
  }, []);

  useEffect(() => {
    if (alcatrazFeatureFlag) {
      if (location?.pathname !== SELF_CARE.main && location?.pathname !== '/') {
        encryptAllData();
      }
    } else {
      decryptAllDataifNeeded();
    }
  }, [location]);

  useEffect(() => {
    const current = window.location.href;
    const last = localStorage.getItem(CURRENT_URL);

    if (last && last !== current) {
      localStorage.setItem(PREVIOUS_URL, last);
    }

    localStorage.setItem(CURRENT_URL, current);
  }, [locationValue]);

  useEffect(() => {
    const isMobileUser = isMobile().any();
    const arrayPage = ['/activar-tv', '/ativar-tv', '/ativar', '/activar', '/preferences', '/404', '/faq', '/mobile-login', '/logout', '/login', '/user', '/home/pdp', '/home/no-entitlement', '/redirect'];
    const storeCode = (userCountry || country || '').toLowerCase();

    const containUrl = arrayPage.filter(
      (obj) => path?.includes(obj) || location?.pathname?.includes(obj),
    )?.length || false;

    if (isMobileUser && !containUrl) {
      window.location.href = `${window.location.origin || 'https://www.directvgo.com'}${(storeCode && !isBrazil) ? `/${storeCode}` : ''}/mobile-login`;
    }
  }, [country, path, location, userCountry]);

  useEffect(() => {
    async function execProfiles() {
      const profilesApi = await backendService.customer.v1.getProfiles();
      if (profilesApi?.status === 200 && profilesApi?.data?.length) {
        if (alcatrazFeatureFlag) {
          setEncryptedData(profilesApi?.data, PROFILES_STORAGE.profiles);
        } else {
          localStorage.setItem(PROFILES_STORAGE.profiles, JSON.stringify(profilesApi?.data ?? []));
          const currentProfile = JSON.parse(localStorage.getItem(PROFILES_STORAGE.profile));
          if (currentProfile) {
            const updatedProfile = profilesApi?.data.find((p) => p.id === currentProfile.id);
            localStorage.setItem(
              PROFILES_STORAGE.profile,
              JSON.stringify(updatedProfile ?? currentProfile ?? {}),
            );
          }
        }
        dispatch(setProfiles(profilesApi.data));
      }
    }
    if (isUserLoggedIn()) {
      execProfiles();
    }
  }, [dispatch]);

  useEffect(() => {
    aemRequestInterval.current = setInterval(
      () => dispatch(api.util.invalidateTags([rtkQueryTags.aem])),
      TIME_UNITS_AS_MILLISECONDS.HOUR,
    );
    return () => clearInterval(aemRequestInterval.current);
  }, [dispatch]);

  useEffect(() => {
    if (!isAEMConfigurationLoading && !isAEMConfigurationError && AEMConfiguration) {
      dispatch(setAEMConfiguration(AEMConfiguration));
    }
  }, [dispatch, AEMConfiguration, isAEMConfigurationLoading, isAEMConfigurationError]);

  useEffect(() => {
    setNuanceDataVariable(country);

    if (isUserLoggedIn() && idToken) {
      const { vrioCustomerId } = jwtDecode(idToken);
      const notificationsPermission = alcatrazFeatureFlag
        ? getEncryptedData(NOTIFICATIONS_PERMISSION_LOCAL_STORAGE_NAME) ?? {}
        : JSON.parse(localStorage.getItem(NOTIFICATIONS_PERMISSION_LOCAL_STORAGE_NAME)) ?? {};
      const { allow: areNotificationsAllowed } = notificationsPermission[vrioCustomerId] || {};

      if (areNotificationsAllowed) {
        (async () => {
          const sdk = await UA;
          await sdk.register();
          sdk.channel.namedUser.set(vrioCustomerId);
        })();
      }
    }
  }, [country, user, idToken]);

  useEffect(() => {
    if (isUserLoggedIn()) {
      const {
        account: {
          uid,
          profile: { firstName, lastName, email },
        },
      } = user;
      datadogRum.setUser({
        id: uid,
        name: `${firstName} ${lastName}`.trim(),
        email,
      });
    } else {
      datadogRum.clearUser();
    }
  }, [user]);

  useEffect(() => {
    const profileStoraged = alcatrazFeatureFlag
      ? getEncryptedData(PROFILES_STORAGE.profile) ?? {}
      : JSON.parse(localStorage.getItem(PROFILES_STORAGE.profile)) ?? {};

    if (!selectedProfile && profileStoraged) {
      dispatch(setSelectedProfile(profileStoraged));
    }

    if (idToken) {
      setGtmUserData(gtmConstantData(idToken, selectedProfile));
    }
  }, [idToken, setGtmUserData, selectedProfile, dispatch]);

  return (
    <IntlProviderWrapper>
      <ChromecastProvider>
        <MqttProvider>
          <div
            id="dtvApp"
            className={classNames('', {
              'dtv-public': !isUserLoggedIn(),
              'dtv-pdp': path.includes(PDP_URL),
            })}
          >
            {isUserLoggedIn() && <ChromecastConnectionBar />}
            {(!isAEMConfigurationLoading && (
              <>
                <div id="dtv-div-notifications" className="dtv-div-notifications" />
                <ScrollToTop />
                <SlowConnection />
                <LoadingBar className="dtv-loading-bar" />
                <ToastNotification />
                <BarNotification />
                <Modal />
                <Route exact component={SearchOverlay} path={`(.*)/${PATHS.SEARCH}`} />
                <Switch>
                  <PrivateRoute exact path={pinLoginUrls.main} component={InsertPinTV} />
                  <PrivateRoute
                    exact
                    path={pinLoginUrls.mainPlusResult}
                    component={ResultInsertPin}
                  />
                  <Redirect exact from={pinLoginUrls.basic} to={pinLoginUrls.main} />
                  <Redirect exact from={`/:country${pinLoginUrls.basic}`} to={pinLoginUrls.main} />
                  <Redirect exact from={`/:country${pinLoginUrls.main}`} to={pinLoginUrls.main} />
                  <Route exact path={LOGOUT_URL} component={Logout} />
                  <Route exact path="/login" render={() => <Landing loginOpened />} />
                  <Route exact path="/expiredSession" component={ExpiredSession} />
                  <Route exact path="/expiredSession/:redirectTo" component={ExpiredSession} />
                  <PrivateRoute
                    exact
                    from={PATHS.HOME}
                    component={Home}
                  />
                  <PrivateRoute path="/home" component={Home} />
                  <PrivateRoute exact path="/player/live/:channelId" component={LivePlayer} />
                  <PrivateRoute exact path={`${ROUTES.hbsPlayer.path}:channelId`} component={FifaVideoPlayer} />
                  <PrivateRoute exact path="/player/:assetType/:assetId" component={VodPlayer} />
                  <PrivateRoute exact path="/user/profile" component={UserProfile} />
                  <PrivateRoute exact path="/user/profile/management" component={UserProfileManagement} />
                  <PrivateRoute exact path="/user/edit/profile/:id" component={UserProfileEdit} />
                  <PrivateRoute exact path="/user/create/profile" component={UserProfileCreate} />
                  <PrivateRoute exact path="/user/onboarding" component={Onboarding} />
                  <PrivateRoute exact path="/user/page/error" component={ProfilesErrorPage} />
                  <PrivateRoute exact path="/user/profile/content/block" component={ProfileContentBlock} />
                  <Route exact path="/" component={Landing} />
                  <Route exact path={`/${TERMS_AND_CONDITIONS_URL}`} component={TermsAndConditions} />
                  <Route exact path={`/${TERMS_OF_SERVICE_URL}/:subpage`} component={TermsOfService} />
                  <Route exact path={`/:country/${TERMS_OF_SERVICE_URL}/:subpage`} component={TermsOfService} />
                  <Route path="/404" component={ErrorPage} />
                  <Route path="/user/error" component={AuthError} />
                  <Route component={ErrorPage} />
                </Switch>
              </>
            )
            )}
          </div>
        </MqttProvider>
      </ChromecastProvider>
    </IntlProviderWrapper>
  );
}

function mapStateToProps({ user, router, global }) {
  const country = getL(user, 'account.country', DEFAULT_VALUES.COUNTRY).toLowerCase();
  const { userCountry } = getL(global, 'configurationAEM.data', '');
  const location = router?.location;

  return {
    user,
    country,
    path: location ? location?.pathname : '',
    urlSearchParams: location ? location?.search : {},
    userCountry,
  };
}

App.propTypes = {
  path: PropTypes.string,
  country: PropTypes.string,
  user: PropTypes.shape({}),
  location: PropTypes.shape({}),
};

App.defaultProps = {
  path: '',
  country: '',
  user: {},
  location: {},
};

export default withRouter(connect(mapStateToProps, {})(App));
